import React, { useState, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import GlassModal from './Common/GlassModal';
import { showToast } from './Common/Toast';
import { validateTimeOffRange } from '../utils/timeOffValidation';

const PTORequestModal = ({ isOpen, onClose, onSubmit, employees = [], user = null, employee = null }) => {
  // If employee is provided, use it directly (from Timesheets page with current user's employee record)
  // If user is provided but not employee, try to use user's id
  // If employees array is provided, this is for admin (Employees page)
  const isIndividualRequest = (user || employee) && !employees.length;

  const blankForm = () => ({
    employeeId: isIndividualRequest ? (employee?.id || user?.id) : '',
    kind: 'VACATION',
    startDate: '',
    endDate: '',
    isFullDay: true,
    startTime: '09:00',
    endTime: '17:00',
    note: ''
  });
  const [formData, setFormData] = useState(blankForm);
  const [loading, setLoading] = useState(false);
  // Board #1945/#1947: an inverted range used to submit and save as zero hours.
  const [rangeError, setRangeError] = useState('');

  // The modal stays mounted while closed, so Cancel / X / Escape left the abandoned dates,
  // note and range error in place for the next "Add Time Off". Start clean on every open.
  useEffect(() => {
    if (isOpen) {
      setFormData(blankForm());
      setRangeError('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Board #1945/#1947. There was no check at all: an end date before the start date, or an end
    // time before the start time on the same day, submitted happily. The day count downstream is
    // a `while (currentDate <= endDate)` walk, so an inverted range counted ZERO business days and
    // wrote hours_requested = 0 — the request looked submitted and reserved nothing, which is why
    // the list rendered Days as "-". Shown inline rather than as a toast so it sits beside the
    // fields the user has to change.
    const message = validateTimeOffRange(formData);
    if (message) {
      setRangeError(message);
      return;
    }
    setRangeError('');
    setLoading(true);

    try {
      // Convert form data to proper datetime format
      const submitData = {
        ...formData,
        startsAt: formData.isFullDay
          ? `${formData.startDate}T00:00:00`
          : `${formData.startDate}T${formData.startTime}:00`,
        endsAt: formData.isFullDay
          ? `${formData.endDate}T23:59:59`
          : `${formData.endDate}T${formData.endTime}:00`
      };

      await onSubmit(submitData);
      setFormData(blankForm());
      onClose();
    } catch (error) {
      console.error('Error submitting PTO request:', error);
      if (window.alert) {
        showToast('error', 'Failed to add time off');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    // A stale range error must not outlive the fields it was about (e.g. both dates cleared,
    // where native `required` blocks submit before handleSubmit can clear it).
    setRangeError('');
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen) return null;

  return (
    <GlassModal
      isOpen={isOpen}
      onClose={onClose}
      maxWidth="max-w-md"
      closeOnOverlay={false}
      header={(
        <>
          <h2 className="text-xl font-semibold text-gray-900">Add Time Off</h2>
          <button type="button" aria-label="Close" title="Close"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </>
      )}
      footer={(
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-900 dark:text-gray-100 border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 hover:bg-gray-50 dark:hover:bg-slate-700/50"
          >
            Cancel
          </button>
          <button
            type="submit"
            form="pto-form"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {loading ? 'Saving...' : 'Add Time Off'}
          </button>
        </div>
      )}
    >

        <form id="pto-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Board #1945/#1947: stated beside the fields, and role="alert" so it is announced
            rather than only seen. */}
        {rangeError && (
          <div role="alert" className="rounded-lg border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/40 px-3 py-2 text-sm text-red-800 dark:text-red-200">
            {rangeError}
          </div>
        )}
          {/* Employee Selection - Only show for admin/manager requests */}
          {!isIndividualRequest && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Employee
              </label>
              <select
                name="employeeId"
                value={formData.employeeId}
                onChange={handleChange}
                required
                className="w-full border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select Employee</option>
                {employees.map(emp => (
                  <option key={emp.id} value={emp.id}>
                    {emp.full_name || emp.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Show current user info for individual requests */}
          {isIndividualRequest && (
            <div className="bg-white/5 backdrop-blur-sm p-3 rounded-md border border-white/10">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Adding Time Off For
              </label>
              <p className="text-sm text-gray-900 font-medium">
                {/* Prefer employee data over user data */}
                {employee?.full_name || 
                  (employee?.first_name && employee?.last_name ? `${employee.first_name} ${employee.last_name}` :
                  user?.full_name || user?.name || 
                  (user?.first_name && user?.last_name ? `${user.first_name} ${user.last_name}` : 
                  user?.email?.split('@')[0] || 'Unknown Employee'))}
              </p>
            </div>
          )}

          {/* Time Off Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <select
              name="kind"
              value={formData.kind}
              onChange={handleChange}
              className="w-full border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="VACATION">🏖️ Vacation</option>
              <option value="SICK">🤒 Sick Leave</option>
              <option value="PERSONAL">👤 Personal Day</option>
              <option value="HOLIDAY">🎉 Holiday</option>
              <option value="BEREAVEMENT">🕊️ Bereavement</option>
              <option value="JURY_DUTY">⚖️ Jury Duty</option>
              <option value="UNPAID">📝 Unpaid Leave</option>
              <option value="FMLA">⚕️ FMLA Leave</option>
              <option value="OTHER">📝 Other</option>
            </select>
          </div>

          {/* Full Day Toggle */}
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                name="isFullDay"
                checked={formData.isFullDay}
                onChange={(e) => { setRangeError(''); setFormData({...formData, isFullDay: e.target.checked}); }}
                className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="text-sm font-medium text-gray-700">Full Day</span>
            </label>
            <p className="text-xs text-gray-500 mt-1">
              {formData.isFullDay ? 'Entire day will be blocked' : 'Specify start and end times'}
            </p>
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date <span className="text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleChange}
              required
              className="w-full border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* End Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date <span className="text-red-500" aria-hidden="true">*</span>
            </label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleChange}
              required
              className="w-full border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Time Selection (only if not full day) */}
          {!formData.isFullDay && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Start Time
                </label>
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                  className="w-full border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  End Time
                </label>
                <input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                  className="w-full border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Note (Optional)
            </label>
            <textarea
              name="note"
              value={formData.note}
              onChange={handleChange}
              rows={3}
              placeholder="Reason for time off..."
              className="w-full border rounded-lg bg-white/70 dark:bg-slate-800/60 border-white/20 dark:border-slate-700/40 px-3 py-2 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </form>
    </GlassModal>
  );
};

export default PTORequestModal;
